import { useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { appLogin } from "../../lib/api";
import { getAppSucursalId, setAppSession } from "../../lib/app-storage";

type Props = {
  open: boolean;
  onUnlock: () => void;
};

// Overlay full-screen que bloquea la pantalla. Se desbloquea con PIN
// (mismo PIN que el de la pantalla — re-loguea internamente).
export function LockScreen({ open, onUnlock }: Props) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const sucursalId = getAppSucursalId();
    if (!sucursalId) {
      setError("Sucursal no detectada. Volvé a /setup.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await appLogin(sucursalId, pin);
      setAppSession(res.token, res.expires_at, sucursalId);
      onUnlock();
      setPin("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPin("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md">
      <div className="w-full max-w-sm rounded-2xl bg-[#1a1540] p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center gap-3">
          <Lock className="h-16 w-16 text-[#a78bfa]" />
          <h2 className="text-2xl font-bold">Pantalla bloqueada</h2>
          <p className="text-center text-sm opacity-70">
            Ingresá el PIN para reactivar la pantalla.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="rounded-lg bg-white/10 px-3 py-3 text-center text-2xl tracking-[0.4em] outline-none focus:ring-2 focus:ring-[#a78bfa]"
            placeholder="••••"
            required
          />
          {error && (
            <div className="rounded-lg bg-red-500/15 px-3 py-2 text-center text-sm text-red-300">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || pin.length === 0}
            className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold transition disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Desbloquear
          </button>
        </form>
      </div>
    </div>
  );
}
